import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Header } from "@/shared/components/header/Header";
import { Icon, Input } from "@orgmym/leeloo-core";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom";

import {
  isChatListQuery,
  useChatStatusCountsQuery,
  useChatsInfiniteQuery
} from "@/features/chat/creator/presentation/pages/chat-list/viewModel";
import type {
  ChatStatus,
  ChatTabType
} from "@/features/chat/domain/repositories/chat";
import {
  ChatList,
  ChatMigrationBanner,
  LeavePrivateMediaConfirmation
} from "@/features/chat/shared/components";
import type { Chat as ChatModel } from "@/features/chat/shared/types";
import { extractReceiverId } from "@/features/chat/shared/utils/chat-helpers";

import { useChatListTracking } from "@/features/chat/creator/presentation/hooks/useTracking";

import * as styles from "./chat-layout.css";
import { ChatSidebarHeader } from "./components";

const CHATS_PAGE_SIZE = 6;
const SEARCH_DEBOUNCE_MS = 400;
const SEARCH_MIN_CHARS = 3;
const TAB_VALUES: ChatTabType[] = [
  "all",
  "unread",
  "to_deliver",
  "pending_request",
  "blocked",
  "muted"
];

export const ChatLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();

  const [selectedChat, setSelectedChat] = useState<ChatModel | undefined>();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  // Navigation guard for private media panel
  const navigationGuardRef = useRef<(() => boolean) | null>(null);
  const [pendingChat, setPendingChat] = useState<ChatModel | null>(null);
  const [showLeaveConfirmModal, setShowLeaveConfirmModal] = useState(false);

  const { track } = useChatListTracking();

  // Debounce search to avoid spamming API; require at least 3 chars to trigger
  useEffect(() => {
    if (searchQuery.length > 0 && searchQuery.length < SEARCH_MIN_CHARS) return;

    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      if (searchQuery.length >= SEARCH_MIN_CHARS) {
        track({
          name: "chat_search_started",
          query: searchQuery,
          fan_id: undefined
        });
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const tabFromUrl = searchParams.get("tab") as ChatTabType | null;
  const [activeTab, setActiveTab] = useState<ChatTabType>(
    tabFromUrl && TAB_VALUES.includes(tabFromUrl) ? tabFromUrl : "all"
  );

  const chatStatus = useMemo<ChatStatus | undefined>(() => {
    return activeTab !== "all" ? (activeTab as ChatStatus) : undefined;
  }, [activeTab]);

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useChatsInfiniteQuery(
    CHATS_PAGE_SIZE,
    debouncedSearchQuery || undefined,
    chatStatus,
    "full"
  );

  useEffect(() => {
    if (!debouncedSearchQuery || isFetching) return;
    track({
      name: "creator_track_chat_search",
      result_count: data?.chats?.length ?? 0,
      tab: activeTab,
      fan_id: undefined
    });
  }, [isFetching, debouncedSearchQuery]);

  const {
    data: statusCountsData,
    isLoading: isLoadingStatusCounts,
    refetch: refetchStatusCounts
  } = useChatStatusCountsQuery();

  const queryClient = useQueryClient();

  const chats = data?.chats ?? [];

  const tabCounts = useMemo(() => {
    const getCount = (value: unknown): number =>
      typeof value === "number" ? value : 0;

    return {
      all: chats.length,
      unread: getCount(statusCountsData?.unread),
      to_deliver: getCount(statusCountsData?.to_deliver),
      pending_request: getCount(statusCountsData?.pending_requests),
      online: chats.filter((c) => c.user.is_online).length,
      blocked: getCount(statusCountsData?.blocked),
      muted: getCount(statusCountsData?.muted),
      disabled: chats.filter((c) => c.is_enabled === false).length
    };
  }, [statusCountsData, chats.length]);

  useEffect(() => {
    const tab = searchParams.get("tab") as ChatTabType | null;
    const nextTab: ChatTabType = tab && TAB_VALUES.includes(tab) ? tab : "all";
    setActiveTab(nextTab);
  }, [searchParams]);

  useEffect(() => {
    if (userId) {
      const chat = chats.find((c) => extractReceiverId(c.id) === userId);
      if (chat) {
        setSelectedChat(chat);
      } else if (!isLoading) {
        if (activeTab === "unread" && searchParams.get("tab") === "unread") {
          const nextSearchParams = new URLSearchParams(searchParams);
          nextSearchParams.delete("tab");
          const nextSearch = nextSearchParams.toString();
          navigate(
            {
              pathname: location.pathname,
              search: nextSearch ? `?${nextSearch}` : ""
            },
            { replace: true }
          );
          setActiveTab("all");
        }
        setSelectedChat(undefined);
      }
    } else {
      setSelectedChat(undefined);
    }
  }, [
    userId,
    chats,
    extractReceiverId,
    activeTab,
    isLoading,
    searchParams,
    navigate,
    location.pathname
  ]);

  const navigateToChat = useCallback(
    (chat: ChatModel) => {
      const receiverId = extractReceiverId(chat.id);

      navigate({
        pathname: `/chat/${receiverId}`,
        search: searchParams.toString() ? `?${searchParams.toString()}` : ""
      });

      setSelectedChat(chat);
    },
    [navigate, searchParams]
  );

  const handleChatSelect = useCallback(
    (chat: ChatModel) => {
      // If a navigation guard is active (e.g. private media panel open), show confirmation
      if (navigationGuardRef.current?.()) {
        setPendingChat(chat);
        setShowLeaveConfirmModal(true);
        return;
      }
      navigateToChat(chat);
    },
    [navigateToChat]
  );

  const handleConfirmLeave = useCallback(() => {
    setShowLeaveConfirmModal(false);
    if (pendingChat) {
      navigateToChat(pendingChat);
      setPendingChat(null);
    }
  }, [pendingChat, navigateToChat]);

  const handleCancelLeave = useCallback(() => {
    setShowLeaveConfirmModal(false);
    setPendingChat(null);
  }, []);

  const handleBackToList = useCallback(() => {
    setSelectedChat(undefined);
    navigate(
      {
        pathname: "/chat",
        search: searchParams.toString() ? `?${searchParams.toString()}` : ""
      },
      { replace: true }
    );
  }, [navigate, searchParams]);

  const handleTabChange = useCallback(
    (tab: ChatTabType) => {
      if (tab === activeTab) return;

      track({
        name: "creator_track_chat_tab",
        tab,
        fan_id: undefined
      });

      void refetchStatusCounts();
      queryClient.removeQueries({ predicate: isChatListQuery });

      setActiveTab(tab);
      setSelectedChat(undefined);

      const nextSearchParams = new URLSearchParams(searchParams);
      tab === "all"
        ? nextSearchParams.delete("tab")
        : nextSearchParams.set("tab", tab);
      const nextSearch = nextSearchParams.toString();

      navigate(
        {
          pathname: "/chat",
          search: nextSearch ? `?${nextSearch}` : ""
        },
        { replace: true }
      );
    },
    [activeTab, navigate, searchParams, refetchStatusCounts, queryClient, track]
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <>
      <Header
        title={t("chat.title", "Chats")}
        withButtonBack={!!userId}
        onClick={handleBackToList}
        className={styles.mobileHeader}
      />

      <div className={styles.chatLayout} data-testid="chat-layout">
        <div className={styles.container} data-testid="chat-layout-container">
          <div
            className={userId ? styles.sidebarWithChat : styles.sidebar}
            data-testid="chat-layout-sidebar"
          >
            <ChatSidebarHeader data-testid="chat-layout-sidebar-header" />

            <ChatMigrationBanner />

            <div className={styles.sidebarSearch}>
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t(
                  "creators_messaging_messages_list_search_bar_description"
                )}
                className={styles.searchInput}
                prefixes={<Icon name="search" />}
                suffixes={
                  searchQuery ? (
                    <Icon
                      name="close"
                      onClick={() => handleSearchChange("")}
                      className={styles.searchClearIcon}
                      data-testid="chat-search-clear"
                    />
                  ) : undefined
                }
              />
            </div>

            <div className={styles.sidebarContent}>
              <ChatList
                chats={chats}
                onChatSelect={handleChatSelect}
                selectedChatId={selectedChat?.id}
                selectedChat={selectedChat}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                loading={isLoading}
                isLoadingMore={isFetchingNextPage}
                totalToLoad={CHATS_PAGE_SIZE}
                onLoadMore={handleLoadMore}
                hasMore={hasNextPage}
                tabCounts={tabCounts}
                isLoadingStatusCounts={isLoadingStatusCounts}
                hasOpenChat={!!userId}
                hasSearchQuery={!!debouncedSearchQuery}
              />
            </div>
          </div>

          <div
            className={userId ? styles.mainWithChat : styles.main}
            data-testid="chat-layout-main"
          >
            <Outlet context={{ navigationGuardRef }} />
          </div>
        </div>
      </div>

      <LeavePrivateMediaConfirmation
        isOpen={showLeaveConfirmModal}
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
      />
    </>
  );
};

export type ChatLayoutOutletContext = {
  navigationGuardRef: React.MutableRefObject<(() => boolean) | null>;
};

export default ChatLayout;
